// pages/profile/CompanyProfile.jsx
// View + inline-edit the company record. Uploads a company logo and a user profile
// picture to the cPanel host (see docs/CPANEL_IMAGE_UPLOAD.md); only the returned path
// is stored on the company row, and it flows into the sidebar, top bar and every
// printed document via getBranding(). Reached from the top-right user pill.
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { companyAPI, uploadAPI } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { Spinner, EmptyState } from "../../components/ui";
import { getBranding } from "../../utils/branding";
import { resizeImageToBlob } from "../../utils/helpers";

const STATUS = {
  1: { label: "Demo Mode", cls: "badge-orange" },
  2: { label: "Demo Expired", cls: "badge-red" },
  3: { label: "Activated", cls: "badge-green" },
  4: { label: "Activation Expired", cls: "badge-red" },
};

const initForm = (c) => ({
  name:       c?.CM_Name       ?? "",
  address:    c?.CM_Address    ?? "",
  city:       c?.CM_City       ?? "",
  contact:    c?.CM_ContactNo  ?? "",
  email:      c?.CM_Email      ?? "",
  user:       c?.CM_User       ?? "",
  logo:       c?.CM_Logo       ?? "",
  profilePic: c?.CM_ProfilePic ?? "",
});

export default function CompanyProfile() {
  const { company, updateCompany } = useAuth();
  const toast = useToast();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(() => initForm(company));

  // Resized JPEG Blobs waiting to be uploaded. Held until Save so cancelling an edit
  // leaves no orphaned files on the host.
  const [pending, setPending] = useState({ logo: null, profilePic: null });

  // Local previews for the pending blobs. Declared above the !company guard below so
  // the hook order stays stable across renders.
  const pendingUrls = useMemo(() => ({
    logo:       pending.logo       ? URL.createObjectURL(pending.logo)       : null,
    profilePic: pending.profilePic ? URL.createObjectURL(pending.profilePic) : null,
  }), [pending]);

  // Revoke the previous batch whenever it's replaced, and on unmount.
  useEffect(() => () => {
    Object.values(pendingUrls).forEach((u) => u && URL.revokeObjectURL(u));
  }, [pendingUrls]);

  if (!company) {
    return (
      <div className="page">
        <EmptyState icon="🏢" title="No company loaded" subtitle="Please sign in again." />
      </div>
    );
  }

  const brand     = getBranding(company);
  const formBrand = getBranding({ CM_Logo: form.logo, CM_ProfilePic: form.profilePic });
  const status    = STATUS[Number(company.CM_Status)] || { label: "Unknown", cls: "badge-blue" };
  const set       = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Preview sources: a freshly-picked image wins. While editing the form is the truth,
  // so "Remove" visibly falls back to the bundled asset; otherwise show what's saved.
  const logoPreview    = pendingUrls.logo       || (editing ? formBrand.logoSrc    : brand.logoSrc);
  const profilePreview = pendingUrls.profilePic || (editing ? formBrand.profilePic : brand.profilePic);

  const handleImage = async (key, file, maxSize) => {
    if (!file) return;
    try {
      const blob = await resizeImageToBlob(file, maxSize, 0.8);
      setPending((p) => ({ ...p, [key]: blob }));
    } catch {
      toast("Could not read that image", "error");
    }
  };

  const clearImage = (key) => {
    setPending((p) => ({ ...p, [key]: null }));
    set(key, "");
  };

  const resetPending = () => setPending({ logo: null, profilePic: null });

  const startEdit = () => { setForm(initForm(company)); resetPending(); setEditing(true); };
  const cancel    = () => { setForm(initForm(company)); resetPending(); setEditing(false); };

  const handleSave = async () => {
    if (!form.name.trim()) return toast("Company name is required", "error");
    setSaving(true);
    try {
      // Upload first. If this throws we abort before touching the company row, so it
      // can never end up pointing at a file that isn't on the host.
      let logo       = form.logo;
      let profilePic = form.profilePic;
      if (pending.logo)       logo       = await uploadAPI.image(pending.logo, "logo");
      if (pending.profilePic) profilePic = await uploadAPI.image(pending.profilePic, "profile");

      const saved = { ...form, logo, profilePic };
      await companyAPI.update(saved);

      // Merge locally so sidebar / top bar / printed receipts update without a reload.
      // updateCompany() forces CM_Id, so a CM_ID-only echo can't log the user out.
      updateCompany({
        CM_Name:       saved.name.trim(),
        CM_Address:    saved.address,
        CM_City:       saved.city,
        CM_ContactNo:  saved.contact,
        CM_Email:      saved.email,
        CM_User:       saved.user,
        CM_Logo:       saved.logo,
        CM_ProfilePic: saved.profilePic,
      });
      setForm(saved);
      resetPending();
      toast("Company profile updated ✓");
      setEditing(false);
    } catch (e) {
      console.error("[CompanyProfile] save error:", e);
      toast(e?.message || "Failed to update profile", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Company Profile</h1>
          <p className="page-sub">Your Shop Details</p>
        </div>
        {!editing ? (
          <button className="btn btn-primary" onClick={startEdit}>✏️ Edit</button>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-ghost" onClick={cancel} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner /> : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {/* Header card: logo + avatar + name + status */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <div
            style={{
              width: 72, height: 72, borderRadius: 12, overflow: "hidden",
              background: "var(--surface2)", border: "1px solid var(--border)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <img src={logoPreview} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>

          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, margin: 0 }}>
                {company.CM_Name || "—"}
              </h2>
              <span className={`badge ${status.cls}`}><span className="badge-dot" />{status.label}</span>
            </div>
            <div className="td-muted" style={{ marginTop: 4, fontSize: 13 }}>
              {[company.CM_City, company.CM_ContactNo].filter(Boolean).join(" · ") || "No contact details yet"}
            </div>
          </div>

          {/* User avatar (profile picture) */}
        
        </div>
      </div>

      {/* Details / edit form */}
      <div className="card">
        {!editing ? (
          <>
            <div className="card-header"><h3 className="card-title">Details</h3></div>
            <div className="detail-grid">
              <Detail label="Company Name"  value={company.CM_Name} />
              <Detail label="Owner / User"  value={company.CM_User} />
              <Detail label="Email"         value={company.CM_Email} />
              <Detail label="Contact No."   value={company.CM_ContactNo} />
              <Detail label="City"          value={company.CM_City} />
              <Detail label="Address"       value={company.CM_Address} />
            </div>
          </>
        ) : (
          <>
            <div className="card-header"><h3 className="card-title">Edit Details</h3></div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input className="form-input" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Owner / User</label>
                <input className="form-input" value={form.user} onChange={(e) => set("user", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Contact No.</label>
                <input className="form-input" value={form.contact} onChange={(e) => set("contact", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-input" value={form.city} onChange={(e) => set("city", e.target.value)} />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Address</label>
                <textarea className="form-textarea" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} />
              </div>

              {/* Image uploads */}
              <div className="form-group">
                <label className="form-label">Company Logo</label>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 56, height: 56, borderRadius: 10, overflow: "hidden", background: "var(--surface2)", border: "1px solid var(--border)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img src={logoPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <input type="file" accept="image/*" onChange={(e) => handleImage("logo", e.target.files?.[0], 512)} />
                    {(form.logo || pending.logo) && <button className="btn btn-ghost btn-xs" onClick={() => clearImage("logo")}>Remove logo</button>}
                  </div>
                </div>
              </div>

              {/* <div className="form-group">
                <label className="form-label">Profile Picture</label>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", overflow: "hidden", background: "var(--surface2)", border: "1px solid var(--border)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {profilePreview
                      ? <img src={profilePreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span className="td-muted" style={{ fontSize: 11 }}>None</span>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <input type="file" accept="image/*" onChange={(e) => handleImage("profilePic", e.target.files?.[0], 256)} />
                    {(form.profilePic || pending.profilePic) && <button className="btn btn-ghost btn-xs" onClick={() => clearImage("profilePic")}>Remove picture</button>}
                  </div>
                </div>
              </div> */}
            </div>
            <p className="td-muted" style={{ fontSize: 12, marginTop: 6 }}>
              Images are resized, then uploaded to the server when you save. The logo is
              used on printed bills and receipts.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="detail-item">
      <label>{label}</label>
      <span>{value || "—"}</span>
    </div>
  );
}
